/**
 * Group Table Column Definitions
 * 
 * TanStack Table column definitions for Groups table
 * Includes custom cell renderers for status badges, dates, and actions
 */

import { ColumnDef } from '@tanstack/react-table';
import { FiEdit2, FiEye, FiSettings } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { StatusBadge } from '@/components/Common/StatusBadge';
import type { GroupListItem, GroupStatus } from '@/types';
import styles from './groupColumns.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Group Actions Props
 */
export interface GroupActionsProps {
  onEdit?: (group: GroupListItem) => void;
  onView?: (group: GroupListItem) => void;
  onManage?: (group: GroupListItem) => void;
}

/**
 * Create group columns for TanStack Table
 */
export const createGroupColumns = (actions: GroupActionsProps): ColumnDef<GroupListItem>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Nome do Grupo',
    cell: ({ row, getValue }) => {
      const name = getValue() as string;
      const initial = name.charAt(0).toUpperCase();
      
      return (
        <div 
          className={styles.groupInfo}
          onClick={() => actions.onView?.(row.original)}
          style={{ cursor: 'pointer' }}
        >
          <div className={styles.groupIcon}>
            {initial}
          </div>
          <span className={styles.groupName}>{name}</span>
        </div>
      );
    },
    enableSorting: true,
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Descrição',
    cell: ({ getValue }) => {
      const description = getValue() as string | undefined;
      return (
        <span className={styles.descriptionCell}>
          {description || <span className={styles.noDescription}>Sem descrição</span>}
        </span>
      );
    },
    enableSorting: false,
  },
  {
    id: 'organizationId',
    accessorKey: 'organizationId',
    header: 'Organização',
    cell: ({ getValue }) => (
      <span className={styles.orgBadge}>
        ID: {getValue() as number}
      </span>
    ),
    enableSorting: true,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as GroupStatus;
      return (
        <StatusBadge status={status} type="group" />
      );
    },
    enableSorting: true,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Criado em',
    cell: ({ getValue }) => (
      <span className={styles.dateCell}>{formatDate(getValue() as string)}</span>
    ),
    enableSorting: true,
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Atualizado em',
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return (
        <span className={styles.dateCell}>
          {value ? formatDate(value) : '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    header: 'Ações',
    cell: ({ row }) => {
      const group = row.original;
      
      return (
        <div className={styles.actionsWrapper}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onView?.(group)}
            title="Visualizar detalhes"
            iconLeft={<FiEye size={16} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit?.(group)}
            title="Editar grupo"
            iconLeft={<FiEdit2 size={16} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onManage?.(group)}
            title="Gerenciar agents e certificados"
            iconLeft={<FiSettings size={16} />}
          />
        </div>
      );
    },
    enableSorting: false,
  },
];
